import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { ProductData, ActionType } from '../types/ServiceRequest';
import { checkSerialNumber } from '../services/api';
import { EtkinlikTipi, IslemAdi } from '../constants/status';
import Toast, { ToastType } from '../components/Toast';
import BarcodeScanner from '../components/BarcodeScanner';
import BuyerInfoModal from '../components/BuyerInfoModal';
import PageTransition from '../components/PageTransition';
import { useUser } from '../contexts/UserContext';
import { useMenu } from '../contexts/MenuContext';
import { formatPhoneNumber, isValidTurkishMobile } from '../utils/phoneUtils';

// Garanti başlatma için sadece iki adım
enum WarrantyStep {
  SERIAL_CHECK = 1,
  PRODUCT_DETAIL = 2
}

export default function WarrantyStart() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { userData } = useUser();
  const { isMenuButtonVisible, isSidebarOpen, isMobile } = useMenu();
  
  // Scroll yönünü takip etmek için state ve ref
  const [showBackButton, setShowBackButton] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const lastScrollTop = useRef(0);
  
  // Geri gitme fonksiyonu - bir önceki adıma dön
  const handleGoBack = () => {
    switch (currentStep) {
      case WarrantyStep.PRODUCT_DETAIL:
        setDirection(-1);
        setCurrentStep(WarrantyStep.SERIAL_CHECK);
        updateUrlParams(WarrantyStep.SERIAL_CHECK);
        break;
      default:
        // İlk adımdaysa tarayıcı geçmişine dön
        navigate(-1);
        break;
    }
  };
  
  // Sayfanın en üstüne scroll fonksiyonu
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // URL parametrelerini güncelleyen yardımcı fonksiyon
  const updateUrlParams = (step: WarrantyStep, replaceHistory: boolean = false) => {
    const newParams = new URLSearchParams(window.location.search);
    
    // Adım parametresini güncelle - ServiceRequest.tsx ile uyumlu
    if (step === WarrantyStep.SERIAL_CHECK) {
      newParams.delete('step');
    } else {
      // İkinci adım için step=1 (ServiceRequest.tsx'teki gibi)
      newParams.set('step', '1');
    }
    
    navigate({ search: newParams.toString() }, { replace: replaceHistory });
  };

  const [currentStep, setCurrentStep] = useState<WarrantyStep>(() => {
    const stepParam = searchParams.get('step');
    // step=1 ise ikinci adım (PRODUCT_DETAIL), yoksa ilk adım
    if (stepParam === '1') {
      return WarrantyStep.PRODUCT_DETAIL;
    }
    return WarrantyStep.SERIAL_CHECK;
  });
  
  const [serialNumber, setSerialNumber] = useState('');
  const [productData, setProductData] = useState<ProductData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [direction, setDirection] = useState(0); // 1 = ileri, -1 = geri
  const [isInitialRender, setIsInitialRender] = useState(true);
  
  // Barkod okuyucu modal state
  const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState(false);
  
  // Alıcı bilgileri modal state
  const [isBuyerInfoModalOpen, setIsBuyerInfoModalOpen] = useState(false);
  
  // Toast ile ilgili state değişkenleri
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'info' as ToastType
  });
  
  // URL parametrelerini dinle ve adımı senkronize et
  useEffect(() => {
    const stepParam = searchParams.get('step');
    // step=1 ise ikinci adım, yoksa ilk adım
    const urlStep = stepParam === '1' ? WarrantyStep.PRODUCT_DETAIL : WarrantyStep.SERIAL_CHECK;
    
    // Sayfa yenilendiğinde veya ilk yüklemede ürün verisi yoksa ilk adıma dön
    if (!productData && urlStep !== WarrantyStep.SERIAL_CHECK) {
      setCurrentStep(WarrantyStep.SERIAL_CHECK);
      updateUrlParams(WarrantyStep.SERIAL_CHECK, true);
      return;
    }
    
    if (urlStep !== currentStep) {
      // Yön belirleme
      if (urlStep > currentStep) {
        setDirection(1);
      } else if (urlStep < currentStep) {
        setDirection(-1);
      }
      
      setCurrentStep(urlStep);
    }
  }, [searchParams, productData]);
  
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);
  
  // Toast gösterme fonksiyonu
  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };
  
  // Toast kapatma fonksiyonu
  const hideToast = () => {
    setToast(prev => ({
      ...prev,
      isVisible: false
    }));
  };

  // Barkod okuyucu handler'ları
  const handleBarcodeScanned = (result: string) => {
    setSerialNumber(result);
    setIsBarcodeScannerOpen(false);
    // Seri numarası değiştiğinde hata mesajını temizle
    if (error) {
      setError('');
    }
    showToast('Barkod başarıyla okundu!', 'success');
  };

  const handleBarcodeScanError = (error: string) => {
    showToast(`Barkod okuma hatası: ${error}`, 'error');
  };

  // Otomatik kontrol fonksiyonu
  const handleAutoCheck = () => {
    // Form submit event'ini simüle et
    const form = document.querySelector('form');
    if (form) {
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(submitEvent);
    }
  };

  // İlk yükleme tamamlandığında isInitialRender'ı false yap
  useEffect(() => {
    setIsInitialRender(false);
  }, []);

  // İlk yükleme tamamlandığında buton gösterimini aktifleştir
  useEffect(() => {
    if (!loading && productData) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 500); // 500ms gecikme
      
      return () => clearTimeout(timer);
    }
  }, [loading, productData]);

  // Scroll olayını dinle - butonu gizle/göster
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      // Modal açıksa butonu gizle
      if (isModalOpen) {
        setShowBackButton(false);
        return;
      }
      
      // Modal kapandıysa, mevcut scroll pozisyonuna göre butonun durumunu yeniden değerlendir
      if (lastScrollTop.current === undefined || lastScrollTop.current === null) {
        setShowBackButton(true);
        lastScrollTop.current = st;
        return;
      }
      
      // Buton için threshold (10px)
      if (st > lastScrollTop.current + 10) {
        setShowBackButton(false);
      } else if (st < lastScrollTop.current - 10) {
        setShowBackButton(true);
      }
      
      // Son scroll pozisyonunu kaydet
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    // Modal kapanma durumunu özel olarak kontrol eden fonksiyon
    const handleModalStateChange = () => {
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      if (!isModalOpen) {
        // Modal kapandı - butonu tekrar göster
        const st = window.pageYOffset || document.documentElement.scrollTop;
        setShowBackButton(true);
        lastScrollTop.current = st;
        
        // Kısa bir delay ile normal scroll davranışını başlat
        setTimeout(() => {
          handleScroll();
        }, 50);
      } else {
        // Modal açıldı - butonu gizle
        setShowBackButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Body attribute değişikliklerini izle (modal açılıp kapanması için)
    const observer = new MutationObserver(handleModalStateChange);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-modal-open']
    });
    
    // İlk kontrol
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  // AliciFaturaTarihi tarih formatına dönüştürme
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    // /Date(1738875600000)/ formatını çıkarıp timestamp elde ediyoruz
    const timestamp = parseInt(dateString.replace(/\/Date\((\d+)\)\//, '$1'), 10);
    if (isNaN(timestamp)) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('tr-TR');
  };

  // API'den gelen tarihi modal formuna uygun formata çevirme
  const formatDateFromApi = (dateString: string | undefined) => {
    if (!dateString) return '';
    const timestamp = parseInt(dateString.replace(/\/Date\((\d+)\)\//, '$1'), 10);
    if (isNaN(timestamp)) return '';
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  // Form'dan gelen tarihi API formatına çevirme
  const formatDateToApi = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const timestamp = date.getTime();
    return `/Date(${timestamp})/`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await checkSerialNumber(serialNumber);

      if (!result.success || !result.data) {
        setError(result.error || 'Bu seri numarasına sahip bir ürün bulunamadı.');
      } else {
        setProductData(result.data);
        setDirection(1); // İleri yönde geçiş
        setCurrentStep(WarrantyStep.PRODUCT_DETAIL);
        updateUrlParams(WarrantyStep.PRODUCT_DETAIL);
      }
    } catch (err) {
      setError('Seri numarası kontrol edilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setDirection(-1); // Geri yönde geçiş
    setCurrentStep(WarrantyStep.SERIAL_CHECK);
    updateUrlParams(WarrantyStep.SERIAL_CHECK);
  };

  // Garanti durumu text'ini döndür
  const getWarrantyStatusText = (status: number | undefined): string => {
    return status === 1 ? 'Garantili' : 'Garanti Dışı';
  };

  // Garanti başlatma gerekip gerekmediğini kontrol et
  const shouldShowWarrantyStart = (productData: ProductData | null): boolean => {
    if (!productData) return false;
    
    // Garanti durumu kontrolü
    if (productData.GarantiDurumu !== 1) return false;
    
    // İptal durumunda garanti başlatılamaz
    if (productData.SonIslem === ActionType.CANCELLED) return false;
    
    // Son işlem durumu kontrolü - ServiceRequest'teki gibi
    const canStartWarranty = (
      (productData.SonIslem === ActionType.CREATED || productData.SonIslem === ActionType.WAITING_FOR_APPROVAL) ||
      (
        productData.IslemAdi === IslemAdi.ONAYLANDI &&
        (productData.SonEtkinlikTipi === EtkinlikTipi.IPTAL_EDILDI)
      )
    );
    
    return canStartWarranty;
  };

  // Garanti durumu mesajını döndür (mobil için)
  const getWarrantyStatusMessage = (productData: ProductData | null, isMobile: boolean = false): string => {
    if (!productData) return '';
    
    if (productData.GarantiDurumu !== 1) {
      return isMobile ? 'Garanti Dışı' : 'Garanti Dışı - Başlatılamaz';
    }
    
    // İptal durumunda
    if (productData.SonIslem === ActionType.CANCELLED) {
      return isMobile ? 'İptal Edildi' : 'İptal Edildi - Başlatılamaz';
    }
    
    // SonIslem 1 ise bilgileri güncelle
    if (productData.SonIslem === ActionType.WAITING_FOR_APPROVAL) {
      return 'Bilgileri Güncelle';
    }
    
    // Müşteri bilgileri zaten girilmişse
    if (productData.SonIslem && 
        productData.SonIslem !== ActionType.CREATED && 
        productData.SonIslem !== ActionType.WAITING_FOR_APPROVAL &&
        !(productData.IslemAdi === IslemAdi.ONAYLANDI && 
          (productData.SonEtkinlikTipi === EtkinlikTipi.IPTAL_EDILDI))) {
      return 'Garanti Zaten Başlatılmış';
    }
    
    return 'Garanti Başlat';
  };

  // Alıcı bilgileri modal handler'ları
  const handleBuyerInfoSuccess = (buyerInfo: any) => {
    showToast('Müşteri bilgileri başarıyla kaydedildi.', 'success');
    setIsBuyerInfoModalOpen(false);
    
    // ProductData'yı forma girilen bilgilerle güncelle
    if (productData) {
      setProductData({
        ...productData,
        AliciFaturaNo: buyerInfo.faturaNo || '',
        AliciTeslimEdilen: buyerInfo.teslimEdilen || '',
        AliciTelefon: buyerInfo.telefon || '',
        AliciAdres: buyerInfo.adres || '',
        AliciEPosta: buyerInfo.email || '',
        AliciBelgeNo: buyerInfo.belgeNo || '',
        AliciTeslimYeri: buyerInfo.teslimYeri || '',
        AliciFaturaTarihi: formatDateToApi(buyerInfo.faturaTarihi) || '',
        AliciTeslimTarihi: formatDateToApi(buyerInfo.teslimTarihi) || ''
      });
    }
    
    // React Query cache'ini güncelle - warranties verilerini yenile
    queryClient.invalidateQueries({ queryKey: ['warranties'] });
    
    // 2 saniye sonra ilk adıma dön ve seri numarasını sıfırla
    setTimeout(() => {
      resetToInitialState();
    }, 2000);
  };

  const handleBuyerInfoError = (error: string) => {
    showToast(error, 'error');
  };

  const handleBuyerInfoAlreadyExists = (updatedBuyerInfo: any, sonIslem: number) => {
    showToast('Bu ürün için müşteri bilgileri zaten girilmiş.', 'success');
    setIsBuyerInfoModalOpen(false);
    
    // ProductData'yı API'den çekilen verilerle güncelle
    if (productData) {
      setProductData({
        ...productData,
        AliciFaturaNo: updatedBuyerInfo.faturaNo || '',
        AliciTeslimEdilen: updatedBuyerInfo.teslimEdilen || '',
        AliciTelefon: updatedBuyerInfo.telefon || '',
        AliciAdres: updatedBuyerInfo.adres || '',
        AliciEPosta: updatedBuyerInfo.email || '',
        AliciBelgeNo: updatedBuyerInfo.belgeNo || '',
        AliciTeslimYeri: updatedBuyerInfo.teslimYeri || '',
        AliciFaturaTarihi: formatDateToApi(updatedBuyerInfo.faturaTarihi) || '',
        AliciTeslimTarihi: formatDateToApi(updatedBuyerInfo.teslimTarihi) || '',
        SonIslem: sonIslem
      });
    }
    
    // React Query cache'ini güncelle - warranties verilerini yenile
    queryClient.invalidateQueries({ queryKey: ['warranties'] });
    
    // 2 saniye sonra ilk adıma dön ve seri numarasını sıfırla
    setTimeout(() => {
      resetToInitialState();
    }, 2000);
  };

  // Garanti başlatma fonksiyonu - artık modal açar
  const handleStartWarranty = () => {
    if (!productData) return;
    setIsBuyerInfoModalOpen(true);
  };

  // İlk duruma sıfırlama fonksiyonu
  const resetToInitialState = () => {
    // Seri numarasını sıfırla
    setSerialNumber('');
    
    // Ürün verilerini sıfırla
    setProductData(null);
    
    // Hataları temizle
    setError('');
    
    // İlk adıma dön
    setDirection(-1);
    setCurrentStep(WarrantyStep.SERIAL_CHECK);
    updateUrlParams(WarrantyStep.SERIAL_CHECK, true);
  };

  const renderSerialNumberStep = () => {
    return (
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center shadow-lg">
            <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9a12.02 12.02 0 00-.382-2.016z" />
            </svg>
          </div>
        </div>

        <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-2 text-center">Seri Numarası Kontrolü</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6 text-center">Garanti başlatmak için seri numarasını girin</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="serialNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Seri Numarası
            </label>
            <div className="relative">
              <input
                id="serialNumber"
                type="text"
                value={serialNumber}
                onChange={(e) => {
                  setSerialNumber(e.target.value);
                  // Seri numarası değiştiğinde hata mesajını temizle
                  if (error) {
                    setError('');
                  }
                }}
                className="form-input appearance-none block w-full pr-12 bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500"
                placeholder="Seri numarasını girin"
                required
              />
              
              {/* Barkod okuyucu butonu */}
              <button
                type="button"
                onClick={() => setIsBarcodeScannerOpen(true)}
                className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M3 7V5a2 2 0 0 1 2-2h2"/>
                  <path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                  <path d="M21 17v2a2 2 0 0 1-2 2h-2"/>
                  <path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                  <rect x="7" y="7" width="10" height="10"/>
                  <rect x="9" y="9" width="6" height="6"/>
                </svg>
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/50 border-l-4 border-red-500 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`
              w-full flex items-center justify-center px-6 py-3.5
              text-base font-semibold text-white
              bg-gradient-to-r from-blue-600 to-indigo-600
              hover:from-blue-700 hover:to-indigo-700
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              rounded-lg shadow-lg shadow-blue-500/25
              transform transition-all duration-200
              hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30
              active:scale-[0.98]
              disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100
              disabled:hover:shadow-lg disabled:hover:shadow-blue-500/25
              relative overflow-hidden
              before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700
            `}
          >
            {loading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Kontrol Ediliyor...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                Kontrol Et
              </div>
            )}
          </button>
        </form>
      </div>
    );
  };

  const renderProductDetailsStep = () => {
    if (!productData) return null;

    return (
      <div className="max-w-md mx-auto">
        <div className="flex justify-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center shadow-lg">
            <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9a12.02 12.02 0 00-.382-2.016z" />
            </svg>
          </div>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white">Ürün Bilgileri</h2>
          <button
            onClick={goBack}
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Geri Dön
          </button>
        </div>

        <div className="border border-indigo-100 dark:border-gray-600 rounded-lg bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600 mb-6">
          <div className="rounded-lg p-6 border border-white dark:border-gray-700 space-y-3">
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Model</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.ModelAdi}</p>
              </div>
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Kategori</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.KategoriAdi}</p>
              </div>
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Renk</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.RenkAdi}</p>
              </div>
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Seri No</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.Sasi}</p>
              </div>
              <div className="mb-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Motor No</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.Motor || '-'}</p>
              </div>
          <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Satıcı Adı</h3>
            <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.SaticiAdi || productData.CariAdi || '-'}</p>
              </div>
              {productData.AliciTeslimEdilen && (
            <div className="pt-3 border-t border-indigo-200 dark:border-gray-600 space-y-3">
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Müşteri Adı</h3>
                    <p className="text-base font-semibold text-gray-900 dark:text-white">{productData.AliciTeslimEdilen}</p>
                  </div>
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Müşteri Telefonu</h3>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {isValidTurkishMobile(productData.AliciTelefon) 
                    ? formatPhoneNumber(productData.AliciTelefon)
                    : productData.AliciTelefon
                  }
                </p>
                  </div>
                  {productData.AliciFaturaTarihi && (
                    <div className="mb-3">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Fatura Tarihi</h3>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {formatDate(productData.AliciFaturaTarihi)}
                      </p>
                    </div>
                  )}
                  {productData.AliciFaturaNo && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Fatura No.</h3>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {productData.AliciFaturaNo}
                      </p>
                    </div>
                  )}                  
                </div>
              )}
          </div>
        </div>

        {/* Garanti Durumu - Ayrı Bölüm */}
        <div className={`border rounded-lg mb-6 ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
            ? 'border-green-400 dark:border-green-400 bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 dark:from-green-900/20 dark:via-green-900/15 dark:to-green-800/20'
            : 'border-red-400 dark:border-red-400 bg-gradient-to-br from-red-50 via-rose-50 to-red-100 dark:from-red-900/20 dark:via-red-900/15 dark:to-red-800/20'
        }`}>
          <div className="rounded-lg p-4 border border-white dark:border-gray-700">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
                  ? 'bg-green-500'
                  : 'bg-red-500'
              }`}>
                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9a12.02 12.02 0 00-.382-2.016z" />
              </svg>
              </div>
            </div>
            <div className="ml-3 flex-1">
              <div className="flex items-center justify-between">
                <h3 className={`text-sm font-medium ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
                     ? 'text-green-800 dark:text-green-200'
                     : 'text-red-800 dark:text-red-200'
                 }`}>
                  Garanti Durumu
                </h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
                     ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700'
                     : 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700'
                 }`}>
                   {(productData.SonIslem === ActionType.CANCELLED) ? 'İptal Edildi' : getWarrantyStatusText(productData.GarantiDurumu)}
                 </span>
              </div>
              
              <div className="mt-2 space-y-1">
                 {productData.GarantiBitisTarihi && (
                  <p className={`text-sm ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
                       ? 'text-green-700 dark:text-green-300'
                       : 'text-red-700 dark:text-red-300'
                   }`}>
                     <span className="font-medium">Bitiş Tarihi:</span> {formatDate(productData.GarantiBitisTarihi)}
                   </p>
                 )}
                 
                 {(productData.GarantiDurumuAciklamasi || productData.SonIslem === ActionType.CANCELLED) && (
                  <p className={`text-sm ${(productData.GarantiDurumu === 1 && productData.SonIslem !== ActionType.CANCELLED)
                       ? 'text-green-700 dark:text-green-300'
                       : 'text-red-700 dark:text-red-300'
                   }`}>
                     <span className="font-medium">Açıklama:</span> {productData.SonIslem === ActionType.CANCELLED ? 'Bu ürün için işlem iptal edilmiştir.' : productData.GarantiDurumuAciklamasi}
                   </p>
                 )}
              </div>
            </div>
          </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* Garanti Başlat düğmesi */}
          <button
            type="button"
            onClick={shouldShowWarrantyStart(productData) ? handleStartWarranty : undefined}
            disabled={!shouldShowWarrantyStart(productData)}
            className={`
              w-full flex items-center justify-center px-6 py-3.5
              text-base font-semibold 
              ${shouldShowWarrantyStart(productData)
                ? 'text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-lg shadow-lg shadow-blue-500/25 transform transition-all duration-200 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30 active:scale-[0.98] relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700'
                : 'text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 cursor-not-allowed'
              }
              rounded-lg
            `}
          >
            <span className="hidden sm:inline">
              {getWarrantyStatusMessage(productData, false)}
            </span>
            <span className="sm:hidden">
              {getWarrantyStatusMessage(productData, true)}
            </span>
          </button>
        </div>
      </div>
    );
  };

  // Yön tabanlı animasyon varyantları
  const variants = {
    enter: (direction: number) => {
      return {
        y: direction > 0 ? 100 : -40,
        opacity: 0
      };
    },
    center: {
      y: 0,
      opacity: 1
    },
    exit: (direction: number) => {
      return {
        y: direction < 0 ? 100 : -40,
        opacity: 0
      };
    }
  };

  return (
    <>
    <PageTransition>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <div className="flex items-center gap-3">
                <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9a12.02 12.02 0 00-.382-2.016z" />
                  </svg>
                </div>
                <div>
                  <h1 className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Garanti Başlatma
                  </h1>
                  <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1 transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Ürününüz için garanti başlatın
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Floating Geri Butonu - Sadece mobilde görünür ve ilk adımda gizli */}
          <AnimatePresence>
            {!loading && !isInitialLoad && showBackButton && !(isMobile && isSidebarOpen) && currentStep !== WarrantyStep.SERIAL_CHECK && (
              <motion.div
                className="fixed top-4 right-4 z-50 md:hidden"
                initial={{ scale: 0, opacity: 0, y: -20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0, opacity: 0, y: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  duration: 0.4
                }}
              >
                <motion.button
                  onClick={handleGoBack}
                  className="relative border border-gray-200 dark:border-gray-700 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-700 dark:hover:to-gray-600 shadow-lg shadow-gray-500/25 dark:shadow-black/25 hover:shadow-xl hover:shadow-gray-500/30 dark:hover:shadow-black/35 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 dark:focus:ring-gray-500 dark:focus:ring-offset-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                  aria-label="Geri Git"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <div className="rounded-full p-2.5 border border-white dark:border-gray-700 text-gray-700 dark:text-gray-300">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  </div>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={variants}
            initial={isInitialRender ? "center" : "enter"}
            animate="center"
            exit="exit"
            transition={{
              y: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg overflow-hidden"
          >
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
              {currentStep === WarrantyStep.SERIAL_CHECK && renderSerialNumberStep()}
              {currentStep === WarrantyStep.PRODUCT_DETAIL && renderProductDetailsStep()}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Barkod Okuyucu Modal */}
      <BarcodeScanner
        isOpen={isBarcodeScannerOpen}
        onClose={() => setIsBarcodeScannerOpen(false)}
        onScan={handleBarcodeScanned}
        onError={handleBarcodeScanError}
        onAutoCheck={handleAutoCheck}
      />

      {/* Alıcı Bilgileri Modal */}
      <BuyerInfoModal
        isOpen={isBuyerInfoModalOpen}
        onClose={() => setIsBuyerInfoModalOpen(false)}
        onSuccess={handleBuyerInfoSuccess}
        onError={handleBuyerInfoError}
        onAlreadyExists={handleBuyerInfoAlreadyExists}
        productId={String(productData?.Id || '')}
        serialNumber={productData?.Sasi || ''}
        productData={productData}
        initialBuyerInfo={productData ? {
          faturaNo: productData.AliciFaturaNo || '',
          belgeNo: productData.AliciBelgeNo || '',
          faturaTarihi: formatDateFromApi(productData.AliciFaturaTarihi) || '',
          teslimTarihi: formatDateFromApi(productData.AliciTeslimTarihi) || '',
          teslimYeri: productData.AliciTeslimYeri || '',
          telefon: productData.AliciTelefon || '',
          teslimEdilen: productData.AliciTeslimEdilen || '',
          email: productData.AliciEPosta || '',
          adres: productData.AliciAdres || ''
        } : undefined}
      />

      </PageTransition>

      {/* Toast Bildirimi - Page Transition dışında */}
      <Toast 
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </>
  );
}
